/**
 * City Admin helpers — city staff use the Admin portal (`/admin/*`).
 * `type: admin` (not `super_admin`) gets city-scoped chrome (city badge, etc.).
 * Legacy `/city-portal/*` URLs redirect via CityPortalLegacyRedirect.
 */

export const CITY_ADMIN_USER_TYPES = ["city_admin", "city-admin"]

const ADMIN_ACTIVE_CITY_KEY = "amok_admin_active_city_id"

const normalizeUserType = user => String(user?.type || "").toLowerCase()

/** City admins use the admin portal with `type: admin`; super admins do not get the city badge. */
export const isCityAdminUser = user => {
  if (!user) return false

  const type = normalizeUserType(user)
  if (type === "super_admin") return false
  if (type === "admin") return true

  return (
    CITY_ADMIN_USER_TYPES.includes(type) ||
    CITY_ADMIN_USER_TYPES.includes(user?.role_slug) ||
    CITY_ADMIN_USER_TYPES.includes(user?.roleSlug)
  )
}

export const getCityPortalHomePath = () => "/admin/dashboard"

/** Assigned cities from GET /me (`with[]=cities`). */
export const getCityAdminCities = user => {
  if (!user) return []
  if (Array.isArray(user.cities) && user.cities.length > 0) {
    return user.cities.filter(city => city?.id)
  }
  if (user.city && typeof user.city === "object" && user.city.id) {
    return [user.city]
  }
  return []
}

export const formatCityAdminLabel = city => {
  if (!city) return "Your City"
  if (typeof city === "string") return city.trim() || "Your City"

  const name = String(city.city || city.name || "").trim()
  const state = String(city.state || "").trim()
  if (name && state) return `${name}, ${state}`
  if (name) return name
  return "Your City"
}

/** Select options for city-admin scoped city pickers (Treasure / Staff). */
export const getCityAdminCitySelectOptions = user =>
  getCityAdminCities(user).map(city => {
    const value = String(city.id)
    const label = formatCityAdminLabel(city)
    return {
      value,
      label: label === "Your City" ? value : label,
    }
  })

export const getStoredAdminActiveCityId = userId => {
  if (!userId || typeof window === "undefined") return ""
  try {
    return (
      localStorage.getItem(`${ADMIN_ACTIVE_CITY_KEY}:${userId}`) ||
      localStorage.getItem(ADMIN_ACTIVE_CITY_KEY) ||
      ""
    )
  } catch {
    return ""
  }
}

export const storeAdminActiveCityId = (userId, cityId) => {
  if (typeof window === "undefined") return
  try {
    const value = cityId ? String(cityId) : ""
    if (userId) {
      localStorage.setItem(`${ADMIN_ACTIVE_CITY_KEY}:${userId}`, value)
    }
    localStorage.setItem(ADMIN_ACTIVE_CITY_KEY, value)
  } catch {
    // Ignore quota / private mode failures.
  }
}

/**
 * Resolve the active city for a city admin from assigned cities + stored preference.
 */
export const resolveCityAdminActiveCity = (user, preferredCityId = "") => {
  const cities = getCityAdminCities(user)
  if (cities.length === 0) return null

  const preferred = preferredCityId || getStoredAdminActiveCityId(user?.id)
  if (preferred) {
    const match = cities.find(city => String(city.id) === String(preferred))
    if (match) return match
  }

  return cities[0]
}

/** Prefer city name from the signed-in user when available. */
export const getCityAdminCityLabel = (user, preferredCityId = "") => {
  if (!user) return "Your City"
  const active = resolveCityAdminActiveCity(user, preferredCityId)
  if (active) return formatCityAdminLabel(active)

  const city =
    user.city_name ||
    user.cityName ||
    (typeof user.city === "string" ? user.city : user.city?.name) ||
    user.active_city?.name ||
    user.activeCity?.name
  const state =
    user.city_state ||
    user.cityState ||
    (typeof user.city === "object" ? user.city?.state : null) ||
    user.active_city?.state ||
    user.activeCity?.state
  if (city && state) return `${city}, ${state}`
  if (city) return city
  return "Your City"
}
